// Auto-numbered part names, shared by the Parts tab, the on-canvas labels and the
// connection/preparation schedules so a part reads the same everywhere.

import type { Lang } from '../shared/types';
import {
  dictDefaults,
  shownName,
} from '../shared/names';

/** Map each object's id to its auto-numbered display name: the base name plus a
 *  two-digit sequence per base name, in drawing order — two "Sauna" parts become
 *  "Sauna 01" / "Sauna 02". `baseNameOf` supplies the (language-specific) base
 *  name, so each caller keeps its own i18n / default-label resolution. Works for
 *  any object with an `id` (parts, walls, polygons). Recomputed from the live
 *  list, so deleting one renumbers the rest that share its name. */
export function numberedNames<T extends { id: string }>(
  items: T[],
  baseNameOf: (item: T) => string,
): Map<string, string> {
  const run = new Map<string, number>();
  const out = new Map<string, string>();
  for (const i of items) {
    const base = baseNameOf(i);
    const n = (run.get(base) ?? 0) + 1;
    run.set(base, n);
    out.set(i.id, `${base} ${String(n).padStart(2, '0')}`);
  }
  return out;
}

/** A drawing object's name in `lang`: the one entered for it, else the word for
 *  its kind (a dictionary key: `polygonWord`, `wallWord`, a flooring's kind) in
 *  `lang` (docs/claude/names.md). */
export function objectName(entered: string | undefined, word: string, lang: Lang, trIn: (key: string, lang: Lang) => string): string {
  return shownName(entered, dictDefaults(word, trIn), lang);
}

/** The word a polygon is called by while no name is entered (a dictionary key):
 *  an ellipse whose points all stand one radius from their middle is a circle. */
export function polygonWord(p: { ellipse?: boolean; points: ReadonlyArray<{ x: number; y: number }> }): 'Circle' | 'Ellipse' | 'Polygon' {
  if (!p.ellipse) return 'Polygon';
  const n = p.points.length;
  if (!n) return 'Ellipse';
  const cx = p.points.reduce((s, q) => s + q.x, 0) / n, cy = p.points.reduce((s, q) => s + q.y, 0) / n;
  const r = p.points.map((q) => Math.hypot(q.x - cx, q.y - cy));
  return Math.max(...r) - Math.min(...r) < 1 ? 'Circle' : 'Ellipse';
}

/** The word a wall or a line is called by while no name is entered. */
export function wallWord(w: { style?: string }): 'Line' | 'Wall' {
  return w.style === 'line' ? 'Line' : 'Wall';
}

/** The default name of an exported file (PDF, DXF, 3D model, print job), read off
 *  the drawing's properties: "DATE PROJECTNO CUSTOMER VERSION" — e.g.
 *  "2026-10-09 C4214 Eran - IBS Las Vegas 4". Blank fields are left out, the
 *  characters a file name cannot hold become "_", and a drawing with none of the
 *  four filled in falls back to `fallback`. */
export function exportBaseName(
  meta: { data?: string; descrizione?: string; commessa?: string; versione?: string } | undefined,
  fallback: string,
): string {
  const parts = [meta?.data, meta?.descrizione, meta?.commessa, meta?.versione]
    .map((s) => (s ?? '').replace(/[/\\:*?"<>|\u0000-\u001f]/g, '_').replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  return parts.length ? parts.join(' ') : fallback;
}
